import dayjs from 'dayjs';
import { Message, MessageBox } from 'element-ui';
import _ from 'lodash';
import config from '@/config/user.env';

/**
 * @description 格式化查询参数
 * @param {Object} data 查询参数
 */
export const formatSearchData = data => {
    const params = _.cloneDeep(data);
    params.createTimeStart = params.createTime ? dayjs(params.createTime[0]).format('YYYY-MM-DD HH:mm:ss') : '';
    params.createTimeEnd = params.createTime ? dayjs(params.createTime[1]).format('YYYY-MM-DD HH:mm:ss') : '';
    params.taskNos = stringToArray(params.taskNos);
    params.customerOrderNos = stringToArray(params.customerOrderNos);
    params.dispatchNos = stringToArray(params.dispatchNos);
    params.siteNames = stringToArray(params.siteName);
    params.siteCodes = stringToArray(params.siteCode);
    params.companyNames = stringToArray(params.companyName);
    params.companyCodes = stringToArray(params.companyCode);
    params.exceptionOperationTypes = stringToArray(params.exceptionOperationTypes);
    delete params.createTime;
    delete params.siteName;
    delete params.siteCode;
    delete params.companyName;
    delete params.companyCode;
    return params;
};

/**
 * @description 将多选查询参数由字符串处理成数组
 * @param {Object} data 查询参数
 */
const stringToArray = data => {
    if (data !== undefined) {
        data = data.length ? data.split(',') : [];
    }
    return data;
};

/**
 * @description 校验查询参数
 * @param {Object} data 查询参数
 */
export const validateSearchData = (data) => {
    // 校验最大查询单号数
    const isTaskNosOk = queryMaxLimit(data.taskNos, 100, '业务单号');
    const isCustomerOrderNosOk = queryMaxLimit(data.customerOrderNos, 100, '客户单号');
    if (!isTaskNosOk || !isCustomerOrderNosOk) {
        return false;
    }
    // 按业务单号、客户单号、发车单号、费率代码、客户主合同号、承运商主合同号查询时，时间不必填,其余条件查询时时间必填
    if (!data.createTimeStart && !data.taskNos.length && !data.customerOrderNos.length && !data.dispatchNos.length &&
        !data.rateCode && !data.customerContractNo && !data.carrierContractNo) {
        Message.warning('创建时间不可为空');
        return false;
    }
    return validateMaxDate(data, 'createTimeStart', 'createTimeEnd');
};

/**
 * @description 校验最大查询单号数
 * @param {Array} data 查询参数
 * @param {String} text 查询参数名称
 */
const queryMaxLimit = (data, max, text) => {
    if (data.length > max) {
        Message.warning(`一次最多查询${max}个${text}`);
        return false;
    };
    return true;
};

export const formatTotalDataList = data => {
    if (!data.length) return [];
    data.forEach(item => {
        item.handldFlag = String(item.handldFlag);
        item.billingTmsFlag = String(item.billingTmsFlag);
    });
    return data;
};

/**
 * @description 校验查询时间跨度不能超过指定日期
 * @param {Object} searchData 查询参数
 * @param {String} sDateField 查询起始时间字段名
 * @param {String} eDateField 查询结束时间字段名
 */
const validateMaxDate = (searchData, sDateField, eDateField) => {
    if (searchData[sDateField] && searchData[eDateField]) {
        const sDateTimeStamp = new Date(searchData[sDateField]).getTime();
        const eDateTimeStamp = new Date(searchData[eDateField]).getTime();
        const maxDay = 1000 * 60 * 60 * 24 * 94; // 按31天来算，组件问题，得加多一天
        if (eDateTimeStamp - sDateTimeStamp > maxDay) {
            Message.warning('按时间查询或导出时，查询或导出时间跨度不可超过三个月');
            return false;
        }
    }
    return true;
};

/**
 * @description 按钮操作 二次确认的消息提示
 * @param {String} tipsInfo 提示信息
 * @param {String} api 调用的接口
 * @param {Array/String/Object} params 接口参数
 * @param {String} successTips 操作成功的提示
 * @param {Object} that 接收函数调用者的 this
 */
export const messageTips = (tipsInfo = '此操作将永久修改该数据, 是否继续？', api, params, successTips = '操作成功', that) => {
    MessageBox.confirm(tipsInfo, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        beforeClose: (action, instance, done) => {
            if (action === 'confirm') {
                instance.confirmButtonLoading = true;
                instance.confirmButtonText = '执行中...';
                api(params)
                    .then(res => {
                        if (res && +res.code === 0) {
                            Message.success(successTips);
                            done();
                        }
                    })
                    .catch((errMsg) => {
                        Message.error(errMsg);
                    })
                    .finally(() => {
                        instance.confirmButtonLoading = false;
                        done();
                        that.getList();
                    });
            } else {
                done();
            }
        }
    }).catch(() => { }); // 添加错误捕获
};

const BcpBaseUrlMap = {
    // SIT: 'https://bcp-sit.annto.com/#',
    SIT: 'https://auth-sit.midea.com/bcp.html#', // BCP系统 sit未部署annto环境
    UAT: 'https://bcp-uat.annto.com/#',
    VER: 'https://bcp-ver.annto.com/#',
    PROD: 'https://bcp.annto.com/#'
};
/**
 * 跳转到中台计费系统的URL
 * @param {string} path Path
 * @param {object} queryObject query object
 * @returns string url
 */
function urlToBcpPage (path, queryObject = {}) {
    const baseURL = BcpBaseUrlMap[config.env || 'UAT'];
    const queryStr = Object.keys(queryObject).map(k => `${k}=${queryObject[k]}`).join('&');
    if (queryStr) {
        return `${baseURL}${path}?${queryStr}`;
    } else {
        return `${baseURL}${path}`;
    }
}

/**
 * 跳转到中台计费系统的URL: 供应商的详情页（供应商运输交易规则详情）
 */
export const urlToBcpSupplierDetail = (rateCode) => urlToBcpPage('/supplierDetail', { rateCode });

/**
 * 跳转到中台计费系统的URL: 客户详情页（客户运输费率详情）
 */
export const urlToBcpConnectionDetail = (rateCode) => urlToBcpPage('/ConnectionDetail', { rateCode });
